import { getSelectedTimeZone } from '@/utils/dateTime';
import { CHART_PALETTE } from './GtChartColors';

// ── ZoomRange ─────────────────────────────────────────────────────────────────
export interface ZoomRange {
    start: number;
    end: number;
}

// ── Internal constants ────────────────────────────────────────────────────────
export const MARGIN_TOP = 20;
export const MARGIN_BOT = 44;
export const MARGIN_RIGHT = 14;
export const TICK_COUNT = 5;
export const MIN_X_LBL_PX = 44;
export const TOOLTIP_W = 224;
export const TOOLTIP_H_EST = 240; // grouped sections + per-row % bars
export const ZOOM_FACTOR = 1.3;
export const MIN_ZOOM_WIN = 1;

// Section palettes — semantic, perceptually-balanced, colorblind-safe.
// Sourced from chartPalette.constant.ts (Digital Blue + Turf Green + Tailwind semantics).
// All colors ~55–65% lightness for equal visual weight on bar charts.
export const DEFAULT_COLORS: string[] = CHART_PALETTE;

// ── Pure math helpers ─────────────────────────────────────────────────────────
export function niceNum(v: number, round: boolean): number {
    if (v <= 0) return 0;
    const exp = Math.floor(Math.log10(v));
    const f = v / Math.pow(10, exp);
    let nf: number;
    if (round) nf = f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10;
    else nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nf * Math.pow(10, exp);
}

export function computeTicks(
    maxVal: number,
    count = TICK_COUNT,
): { ticks: number[]; niceMax: number } {
    if (maxVal <= 0) return { ticks: [0, 2, 4, 6, 8, 10], niceMax: 10 };
    const range = niceNum(maxVal * 1.08, false);
    const step = niceNum(range / (count - 1), true);
    const niceMax = Math.ceil(maxVal / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= niceMax + step * 0.001; v += step) ticks.push(+v.toFixed(10));
    return { ticks, niceMax };
}

export function fmtNum(v: number): string {
    if (!Number.isFinite(v)) return '—';
    if (v >= 1_000_000) return `${+(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${+(v / 1_000).toFixed(1)}K`;
    return String(v);
}

/** Build a smooth bezier curve path through an array of [x,y] points */
export function smoothBezier(pts: [number, number][], tension = 0.3): string {
    if (pts.length === 0) return '';
    if (pts.length === 1) return `M ${pts[0][0]} ${pts[0][1]}`;
    let d = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const [x0, y0] = pts[i];
        const [x1, y1] = pts[i + 1];
        const cpx1 = x0 + (x1 - x0) * tension;
        const cpx2 = x1 - (x1 - x0) * tension;
        d += ` C ${cpx1} ${y0},${cpx2} ${y1},${x1} ${y1}`;
    }
    return d;
}

/** SVG arc path for a pie/donut slice */
export function arcPath(
    cx: number,
    cy: number,
    outerR: number,
    innerR: number,
    startAngle: number,
    endAngle: number,
): string {
    // Clamp tiny slices to avoid degenerate arcs
    const span = Math.max(endAngle - startAngle, 0.0001);
    const sa = startAngle;
    const ea = startAngle + span;
    const cos = Math.cos;
    const sin = Math.sin;
    const large = span > Math.PI ? 1 : 0;

    const ox1 = cx + outerR * cos(sa);
    const oy1 = cy + outerR * sin(sa);
    const ox2 = cx + outerR * cos(ea);
    const oy2 = cy + outerR * sin(ea);

    if (innerR > 0) {
        const ix1 = cx + innerR * cos(ea);
        const iy1 = cy + innerR * sin(ea);
        const ix2 = cx + innerR * cos(sa);
        const iy2 = cy + innerR * sin(sa);
        return `M ${ox1} ${oy1} A ${outerR} ${outerR} 0 ${large} 1 ${ox2} ${oy2} L ${ix1} ${iy1} A ${innerR} ${innerR} 0 ${large} 0 ${ix2} ${iy2} Z`;
    }
    return `M ${cx} ${cy} L ${ox1} ${oy1} A ${outerR} ${outerR} 0 ${large} 1 ${ox2} ${oy2} Z`;
}

// ── Auto date-format helpers (used when no formatX / formatTooltipTitle provided) ──

// Shared month name tables — module-level so they are allocated once.
const _MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const _MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;

/**
 * Split a week-range string into [startDateStr, endDateStr].
 * Handles all separator variants the API may return:
 *   "2026-06-27 - 2026-07-03"   (hyphen)
 *   "2026-06-27 – 2026-07-03"   (en-dash)
 *   "2026-06-27 to 2026-07-03"  (word, as returned by some API versions)
 * Returns null if the string is not a recognised week range.
 */
function _splitWeekRange(raw: string): [string, string] | null {
    const m = raw.match(/^(\d{4}-\d{2}-\d{2})\s+(?:to|\u2013|-)\s+(\d{4}-\d{2}-\d{2})$/);
    if (!m) return null;
    return [m[1], m[2]];
}

/**
 * Returns true if the value looks like any supported date format:
 *   - Unix timestamp (10–13 digits)
 *   - Day:   YYYY-MM-DD  (or with time component)
 *   - Week:  "YYYY-MM-DD - YYYY-MM-DD" / "YYYY-MM-DD – YYYY-MM-DD"
 *   - Month: YYYY-MM
 *   - Year:  YYYY (4-digit string)
 */
export function _isDateLike(v: string | number): boolean {
    if (typeof v === 'number') return /^\d{10,13}$/.test(String(v));
    const s = String(v).trim();
    return (
        /^\d{10,13}$/.test(s) ||
        /^\d{4}-\d{2}-\d{2}/.test(s) ||   // day or ISO with time
        /^\d{4}-\d{2}$/.test(s) ||          // month groupBy: "2026-06"
        /^\d{4}$/.test(s) ||                // year groupBy:  "2026"
        _splitWeekRange(s) !== null          // week groupBy:  "2026-06-27 - 2026-07-03"
    );
}

// Cache Intl.DateTimeFormat instances — creating them is expensive (locale/TZ lookup).
const _fmtCache = new Map<string, Intl.DateTimeFormat>();
function _getCachedFmt(tz: string, withYear: boolean): Intl.DateTimeFormat {
    const key = `${tz}:${withYear ? 'y' : 'n'}`;
    let fmt = _fmtCache.get(key);
    if (!fmt) {
        fmt = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            month: 'short',
            day: 'numeric', // 'numeric' → no leading zero ("Jul 1" not "Jul 01")
            ...(withYear ? { year: 'numeric' } : {}),
        });
        _fmtCache.set(key, fmt);
    }
    return fmt;
}

/**
 * Short x-axis tick label.
 * Week groupBy returns a two-line label (separated by "\n") so both the
 * start and end date are visible and users know each bar covers a full week.
 *
 * groupBy  range string example          →  label
 * day      "2026-07-01"                  →  "Jul 1"
 * week     "2026-06-27 - 2026-07-03"     →  "Jun 27\nJul 3"  (two lines)
 * month    "2026-06"                     →  "Jun '26"
 * year     "2026"                        →  "2026"
 */
export function _autoFmtShort(v: string | number): string {
    const raw = String(v).trim();
    if (!raw) return '';

    // Unix timestamp
    if (/^\d{10,13}$/.test(raw)) {
        const n = Number(raw);
        const ms = n < 1_000_000_000_000 ? n * 1000 : n;
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, false);
    }

    // Week range: two-line label so users see the full week span, not just one day
    //   "2026-06-27 - 2026-07-03" → "Jun 27\nJul 3"
    const weekParts = _splitWeekRange(raw);
    if (weekParts) {
        const sm = weekParts[0].match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const em = weekParts[1].match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (sm && em) {
            const startLbl = `${_MONTHS_SHORT[Number(sm[2]) - 1]} ${Number(sm[3])}`;
            const endLbl = `${_MONTHS_SHORT[Number(em[2]) - 1]} ${Number(em[3])}`;
            return `${startLbl}\n${endLbl}`;
        }
    }

    // Month: "2026-06" → "Jun '26"
    const mm = raw.match(/^(\d{4})-(\d{2})$/);
    if (mm) return `${_MONTHS_SHORT[Number(mm[2]) - 1]} '${mm[1].slice(2)}`;

    // Year: "2026" → "2026"
    if (/^\d{4}$/.test(raw)) return raw;

    // Day: "2026-07-01" → "Jul 1"  (no leading zero, no UTC-offset shift)
    const dm = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dm) return `${_MONTHS_SHORT[Number(dm[2]) - 1]} ${Number(dm[3])}`;

    // ISO with time component
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, false);
    }

    return raw;
}

/**
 * Full tooltip title label.
 *
 * groupBy  range string example          →  tooltip title
 * day      "2026-07-01"                  →  "Jul 1, 2026"
 * week     "2026-06-27 - 2026-07-03"     →  "Jun 27 – Jul 3, 2026"
 * month    "2026-06"                     →  "June 2026"
 * year     "2026"                        →  "2026"
 */
export function _autoFmtFull(v: string | number): string {
    const raw = String(v).trim();
    if (!raw) return '';

    // Unix timestamp
    if (/^\d{10,13}$/.test(raw)) {
        const n = Number(raw);
        const ms = n < 1_000_000_000_000 ? n * 1000 : n;
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, true);
    }

    // Week range → "Jun 27 – Jul 3, 2026"  (or "Jun 27 – 30, 2026" for same month)
    const weekParts = _splitWeekRange(raw);
    if (weekParts) {
        const s = weekParts[0].match(/^(\d{4})-(\d{2})-(\d{2})$/);
        const e = weekParts[1].match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (s && e) {
            const sm = _MONTHS_SHORT[Number(s[2]) - 1];
            const em = _MONTHS_SHORT[Number(e[2]) - 1];
            const sDay = Number(s[3]);
            const eDay = Number(e[3]);
            const yr = e[1]; // use end year (handles cross-year week edges)
            // Same month: "Jun 27 – 30, 2026"
            if (s[1] === e[1] && s[2] === e[2]) return `${sm} ${sDay} – ${eDay}, ${yr}`;
            // Different months: "Jun 27 – Jul 3, 2026"
            return `${sm} ${sDay} – ${em} ${eDay}, ${yr}`;
        }
    }

    // Month: "2026-06" → "June 2026"
    const mm = raw.match(/^(\d{4})-(\d{2})$/);
    if (mm) return `${_MONTHS_FULL[Number(mm[2]) - 1]} ${mm[1]}`;

    // Year: "2026" → "2026"
    if (/^\d{4}$/.test(raw)) return raw;

    // Day: "2026-07-01" → "Jul 1, 2026"  (no leading zero)
    const dm = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dm) return `${_MONTHS_SHORT[Number(dm[2]) - 1]} ${Number(dm[3])}, ${dm[1]}`;

    // ISO with time component
    if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
        const d = new Date(raw);
        if (!Number.isNaN(d.getTime())) return _fmtDate(d, true);
    }

    return raw;
}

function _fmtDate(d: Date, withYear: boolean): string {
    try {
        return _getCachedFmt(getSelectedTimeZone(), withYear).format(d);
    } catch {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short', day: 'numeric', // 'numeric' → no leading zero
            ...(withYear ? { year: 'numeric' } : {}),
        }).format(d);
    }
}
